import { geoCentroid, geoMercator, geoPath } from "d3-geo";
import { useMemo, useRef, useState } from "react";
import { anchoredScrollPosition, createTouchPaintRollback, MAX_MAP_ZOOM, MIN_MAP_ZOOM, paintActionChangesSelection, steppedMapZoom, zoomFromPinch } from "./mapGestures";
import { TIER_BY_VALUE } from "./tiers";
import type { NeighborhoodCollection, NeighborhoodFeature, Rating, Ratings } from "./types";

interface PhillyMapProps {
  neighborhoods: NeighborhoodCollection;
  ratings: Ratings;
  selectedId: string | null;
  selectedIds: string[];
  selectMany: boolean;
  interactionDisabled: boolean;
  onSelect: (id: string, source: "pointer" | "keyboard") => void;
  onNavigate: (id: string | null) => void;
  onPaint: (id: string, action: "add" | "remove") => void;
  onSelectionComplete: () => void;
  onModeChange: (multiple: boolean) => void;
  onClearSelection: () => void;
}

const WIDTH = 680;
const HEIGHT = 720;

interface PointerPoint {
  x: number;
  y: number;
}

interface PinchState {
  startDistance: number;
  startZoom: number;
  anchorX: number;
  anchorY: number;
}

interface PendingTouchPaint {
  pointerId: number;
  id: string;
  action: "add" | "remove";
  x: number;
  y: number;
}

export function PhillyMap({ neighborhoods, ratings, selectedId, selectedIds, selectMany, interactionDisabled, onSelect, onNavigate, onPaint, onSelectionComplete, onModeChange, onClearSelection }: PhillyMapProps) {
  const [zoom, setZoom] = useState<number>(MIN_MAP_ZOOM);
  const scrollRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const zoomRef = useRef<number>(MIN_MAP_ZOOM);
  const scrollFrameRef = useRef<number | null>(null);
  const pathRefs = useRef(new Map<string, SVGPathElement>());
  const panRef = useRef<{ pointerId: number; x: number; y: number; left: number; top: number; moved: boolean } | null>(null);
  const activePointersRef = useRef(new Map<number, PointerPoint>());
  const pinchRef = useRef<PinchState | null>(null);
  const pendingTouchPaintRef = useRef<PendingTouchPaint | null>(null);
  const touchPaintedIdsRef = useRef(new Map<string, "add" | "remove">());
  const touchPaintActiveRef = useRef(false);
  const touchPaintStartRef = useRef<{ selectedId: string | null } | null>(null);
  const suppressClickRef = useRef(false);
  const paintActionRef = useRef<"add" | "remove" | null>(null);
  const projection = useMemo(
    () => geoMercator().fitExtent([[32, 30], [WIDTH - 32, HEIGHT - 30]], neighborhoods),
    [neighborhoods],
  );
  const path = useMemo(() => geoPath(projection), [projection]);
  const points = useMemo(
    () => new Map(neighborhoods.features.map((feature) => [String(feature.properties.id), projection(geoCentroid(feature))!])),
    [neighborhoods, projection],
  );
  const selectedFeature = selectedId
    ? neighborhoods.features.find((feature) => String(feature.properties.id) === selectedId)
    : undefined;
  const selectedPoint = selectedFeature ? points.get(String(selectedFeature.properties.id)) : null;
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  function applyZoomAt(element: HTMLDivElement, nextZoom: number, viewportX: number, viewportY: number, anchorX?: number, anchorY?: number) {
    const currentZoom = zoomRef.current;
    const contentX = anchorX ?? (element.scrollLeft + viewportX) / currentZoom;
    const contentY = anchorY ?? (element.scrollTop + viewportY) / currentZoom;
    zoomRef.current = nextZoom;
    element.dataset.zoomed = nextZoom > MIN_MAP_ZOOM ? "true" : "false";
    if (svgRef.current) svgRef.current.style.width = `${nextZoom * 100}%`;
    setZoom(nextZoom);
    const nextScrollLeft = anchoredScrollPosition(contentX, viewportX, nextZoom);
    const nextScrollTop = anchoredScrollPosition(contentY, viewportY, nextZoom);
    if (scrollFrameRef.current !== null) cancelAnimationFrame(scrollFrameRef.current);
    scrollFrameRef.current = requestAnimationFrame(() => {
      const maxScrollLeft = Math.max(0, element.scrollWidth - element.clientWidth);
      const maxScrollTop = Math.max(0, element.scrollHeight - element.clientHeight);
      element.scrollLeft = Math.min(nextScrollLeft, maxScrollLeft);
      element.scrollTop = Math.min(nextScrollTop, maxScrollTop);
      scrollFrameRef.current = null;
    });
  }

  function zoomFromControl(direction: "in" | "out") {
    const element = scrollRef.current;
    const nextZoom = steppedMapZoom(zoomRef.current, direction);
    if (!element) {
      zoomRef.current = nextZoom;
      setZoom(nextZoom);
      return;
    }
    applyZoomAt(element, nextZoom, element.clientWidth / 2, element.clientHeight / 2);
  }

  function beginPinch(element: HTMLDivElement) {
    const [first, second] = [...activePointersRef.current.values()];
    if (!first || !second) return;
    const rect = element.getBoundingClientRect();
    const midpointX = (first.x + second.x) / 2 - rect.left;
    const midpointY = (first.y + second.y) / 2 - rect.top;
    pinchRef.current = {
      startDistance: Math.hypot(second.x - first.x, second.y - first.y),
      startZoom: zoomRef.current,
      anchorX: (element.scrollLeft + midpointX) / zoomRef.current,
      anchorY: (element.scrollTop + midpointY) / zoomRef.current,
    };
    rollbackTouchPaint();
    panRef.current = null;
    pendingTouchPaintRef.current = null;
    paintActionRef.current = null;
    suppressClickRef.current = true;
    element.dataset.dragging = "true";
  }

  function paintWithTouch(id: string, action: "add" | "remove") {
    const changesSelection = paintActionChangesSelection(action, selectedSet.has(id));
    if (changesSelection && !touchPaintedIdsRef.current.has(id)) touchPaintedIdsRef.current.set(id, action);
    onPaint(id, action);
  }

  function rollbackTouchPaint() {
    const start = touchPaintStartRef.current;
    if (start) {
      const rollback = createTouchPaintRollback(touchPaintedIdsRef.current, start.selectedId);
      for (const change of rollback.changes) {
        onPaint(change.id, change.action);
      }
      onNavigate(rollback.selectedId);
    }
    touchPaintedIdsRef.current.clear();
    touchPaintActiveRef.current = false;
    touchPaintStartRef.current = null;
  }

  function endPan(element: HTMLDivElement, pointerId: number) {
    const pan = panRef.current;
    if (!pan || pan.pointerId !== pointerId) return;
    suppressClickRef.current = pan.moved;
    if (pan.moved) window.setTimeout(() => { suppressClickRef.current = false; }, 0);
    panRef.current = null;
    element.removeAttribute("data-dragging");
    if (element.hasPointerCapture(pointerId)) element.releasePointerCapture(pointerId);
  }

  return (
    <div className="map-stage">
      <div className="map-mode" aria-label="Map interaction mode">
        <button type="button" aria-pressed={!selectMany} onClick={() => onModeChange(false)}>Move map</button>
        <button type="button" aria-pressed={selectMany} disabled={interactionDisabled} onClick={() => onModeChange(true)}>{interactionDisabled ? "Read-only" : "Select several"}</button>
        {selectMany && selectedIds.length ? <button type="button" className="map-clear" onClick={onClearSelection}>Clear {selectedIds.length}</button> : null}
      </div>
      {selectMany ? (
        <div className="map-selection-tip" aria-live="polite">
          <strong>{selectedIds.length ? `${selectedIds.length} selected` : "Select neighborhoods"}</strong>
          <span>Click or drag across the map, then choose a rating.</span>
        </div>
      ) : null}
      <div className="map-zoom" aria-label="Map zoom controls">
        <button type="button" onClick={() => zoomFromControl("out")} disabled={zoom <= MIN_MAP_ZOOM} aria-label="Zoom map out">−</button>
        <span aria-live="polite">{formatZoom(zoom)}×</span>
        <button type="button" onClick={() => zoomFromControl("in")} disabled={zoom >= MAX_MAP_ZOOM} aria-label="Zoom map in">+</button>
      </div>
      <div
        ref={scrollRef}
        className="map-scroll"
        data-zoomed={zoom > 1 ? "true" : "false"}
        data-mode={selectMany ? "select" : "move"}
        onPointerDownCapture={(event) => {
          if (event.pointerType !== "touch" || event.button !== 0) return;
          activePointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
          if (activePointersRef.current.size === 2) {
            event.preventDefault();
            for (const pointerId of activePointersRef.current.keys()) {
              if (!event.currentTarget.hasPointerCapture(pointerId)) event.currentTarget.setPointerCapture(pointerId);
            }
            beginPinch(event.currentTarget);
          }
        }}
        onPointerDown={(event) => {
          if (selectMany || event.button !== 0) return;
          if (event.pointerType === "touch" && activePointersRef.current.size > 1) return;
          panRef.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, left: event.currentTarget.scrollLeft, top: event.currentTarget.scrollTop, moved: false };
        }}
        onPointerMove={(event) => {
          if (event.pointerType === "touch" && activePointersRef.current.has(event.pointerId)) {
            activePointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
          }
          const pinch = pinchRef.current;
          if (pinch && activePointersRef.current.size >= 2) {
            const [first, second] = [...activePointersRef.current.values()];
            const rect = event.currentTarget.getBoundingClientRect();
            const midpointX = (first.x + second.x) / 2 - rect.left;
            const midpointY = (first.y + second.y) / 2 - rect.top;
            const distance = Math.hypot(second.x - first.x, second.y - first.y);
            const nextZoom = zoomFromPinch(pinch.startZoom, pinch.startDistance, distance);
            event.preventDefault();
            applyZoomAt(event.currentTarget, nextZoom, midpointX, midpointY, pinch.anchorX, pinch.anchorY);
            return;
          }
          const pendingPaint = pendingTouchPaintRef.current;
          if (selectMany && pendingPaint?.pointerId === event.pointerId) {
            if (Math.hypot(event.clientX - pendingPaint.x, event.clientY - pendingPaint.y) < 5) return;
            touchPaintActiveRef.current = true;
            paintWithTouch(pendingPaint.id, pendingPaint.action);
            paintActionRef.current = pendingPaint.action;
            pendingTouchPaintRef.current = null;
          }
          if (selectMany && paintActionRef.current) {
            const element = document.elementFromPoint(event.clientX, event.clientY)?.closest<SVGPathElement>(".neighborhood");
            const id = element?.dataset.neighborhoodId;
            if (id) {
              if (event.pointerType === "touch" && touchPaintActiveRef.current) paintWithTouch(id, paintActionRef.current);
              else onPaint(id, paintActionRef.current);
            }
            return;
          }
          const pan = panRef.current;
          if (!pan || pan.pointerId !== event.pointerId) return;
          const deltaX = event.clientX - pan.x;
          const deltaY = event.clientY - pan.y;
          if (!pan.moved && Math.hypot(deltaX, deltaY) < 5) return;
          if (!pan.moved) {
            pan.moved = true;
            event.currentTarget.setPointerCapture(event.pointerId);
          }
          event.currentTarget.dataset.dragging = "true";
          event.currentTarget.scrollLeft = pan.left - deltaX;
          event.currentTarget.scrollTop = pan.top - deltaY;
        }}
        onPointerUp={(event) => {
          const pendingPaint = pendingTouchPaintRef.current;
          const completedTouchSelection = !pinchRef.current && (pendingPaint?.pointerId === event.pointerId || touchPaintActiveRef.current);
          if (!pinchRef.current && pendingPaint?.pointerId === event.pointerId) paintWithTouch(pendingPaint.id, pendingPaint.action);
          if (pendingPaint?.pointerId === event.pointerId) pendingTouchPaintRef.current = null;
          paintActionRef.current = null;
          touchPaintedIdsRef.current.clear();
          touchPaintActiveRef.current = false;
          touchPaintStartRef.current = null;
          activePointersRef.current.delete(event.pointerId);
          if (pinchRef.current) {
            pinchRef.current = null;
            suppressClickRef.current = true;
            window.setTimeout(() => { suppressClickRef.current = false; }, 0);
            event.currentTarget.removeAttribute("data-dragging");
          }
          endPan(event.currentTarget, event.pointerId);
          if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
          if (completedTouchSelection) onSelectionComplete();
        }}
        onPointerCancel={(event) => {
          pendingTouchPaintRef.current = null;
          paintActionRef.current = null;
          rollbackTouchPaint();
          activePointersRef.current.delete(event.pointerId);
          pinchRef.current = null;
          event.currentTarget.removeAttribute("data-dragging");
          endPan(event.currentTarget, event.pointerId);
          if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
        }}
      >
        <svg
          ref={svgRef}
          className="philly-map"
          data-zoomed={zoom > 1 ? "true" : "false"}
          style={{ width: `${zoom * 100}%` }}
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          role="group"
          aria-label="Interactive map of 159 Philadelphia neighborhoods"
          aria-describedby="map-help"
          onPointerUp={() => { paintActionRef.current = null; }}
          onPointerLeave={() => { paintActionRef.current = null; }}
        >
      <desc>Click a neighborhood to rate it. In Move map mode, drag to pan. In Select several mode, click or drag across multiple neighborhoods.</desc>
      <defs>
        <filter id="paper-shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="8" stdDeviation="8" floodColor="#111820" floodOpacity="0.16" />
        </filter>
        <pattern id="rating-probably-not" patternUnits="userSpaceOnUse" width="8" height="8">
          <rect width="8" height="8" fill="#4b5355" />
          <path d="M-2 2L2-2M0 8L8 0M6 10L10 6" stroke="#a5acaf" strokeWidth="1.5" opacity=".65" />
        </pattern>
        <pattern id="rating-maybe" patternUnits="userSpaceOnUse" width="9" height="9">
          <rect width="9" height="9" fill="#a5acaf" />
          <circle cx="4.5" cy="4.5" r="1.35" fill="#4b5355" opacity=".7" />
        </pattern>
        <pattern id="rating-yes" patternUnits="userSpaceOnUse" width="8" height="8">
          <rect width="8" height="8" fill="#8bcaa5" />
          <path d="M-2 2L2-2M0 8L8 0M6 10L10 6" stroke="#007a33" strokeWidth="1.5" opacity=".7" />
        </pattern>
      </defs>
      <g className="map-shape" filter="url(#paper-shadow)">
        {neighborhoods.features.map((feature) => {
          const id = String(feature.properties.id);
          const rating = ratings[id];
          return (
            <path
              key={id}
              ref={(element) => {
                if (element) pathRefs.current.set(id, element);
                else pathRefs.current.delete(id);
              }}
              d={path(feature) ?? undefined}
              className="neighborhood"
              data-rating={rating ?? "unrated"}
              data-selected={selectedSet.has(id) ? "true" : "false"}
              data-neighborhood-id={id}
              role="button"
              tabIndex={id === selectedId ? 0 : -1}
              aria-label={ariaLabel(feature, rating)}
              aria-pressed={selectedSet.has(id)}
              onPointerDown={(event) => {
                if (!selectMany || interactionDisabled || event.button !== 0) return;
                const action = selectedSet.has(id) ? "remove" : "add";
                if (event.pointerType === "touch") {
                  if (activePointersRef.current.size > 1) return;
                  touchPaintedIdsRef.current.clear();
                  touchPaintActiveRef.current = false;
                  touchPaintStartRef.current = { selectedId };
                  pendingTouchPaintRef.current = { pointerId: event.pointerId, id, action, x: event.clientX, y: event.clientY };
                  return;
                }
                event.preventDefault();
                paintActionRef.current = action;
                onPaint(id, action);
                onSelectionComplete();
              }}
              onPointerEnter={(event) => {
                if (!selectMany || !paintActionRef.current || event.buttons !== 1) return;
                onPaint(id, paintActionRef.current);
              }}
              onPointerUp={() => { paintActionRef.current = null; }}
              onClick={(event) => {
                if (suppressClickRef.current) {
                  suppressClickRef.current = false;
                  return;
                }
                if (selectMany && event.detail > 0) return;
                onSelect(id, "pointer");
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onSelect(id, "keyboard");
                } else if (["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) {
                  event.preventDefault();
                  const nextId = directionalNeighbor(id, event.key, points);
                  if (nextId) {
                    onNavigate(nextId);
                    requestAnimationFrame(() => pathRefs.current.get(nextId)?.focus());
                  }
                }
              }}
            >
              <title>{ariaLabel(feature, rating)}</title>
            </path>
          );
        })}
      </g>
      {!selectMany && selectedFeature && selectedPoint ? (
        <g className="map-label" transform={`translate(${selectedPoint[0]} ${selectedPoint[1]}) scale(${1 / zoom})`} aria-hidden="true">
          <rect x="-100" y="-34" width="200" height="28" rx="14" />
          <text textAnchor="middle" y="-20" dominantBaseline="middle">{selectedFeature.properties.name}</text>
          <path d="M -7 -8 L 0 0 L 7 -8 Z" />
        </g>
      ) : null}
        </svg>
      </div>
      <p className="map-help" id="map-help"><strong>Select several:</strong> choose that mode, then click, tap, or drag across neighborhoods and pick one rating for the whole group. On a phone, pinch to zoom; use Move map to drag around.</p>
    </div>
  );
}

function formatZoom(zoom: number): string {
  return Number.isInteger(zoom) ? String(zoom) : zoom.toFixed(1);
}

function directionalNeighbor(
  currentId: string,
  key: string,
  points: Map<string, [number, number]>,
): string | null {
  const current = points.get(currentId);
  if (!current) return null;
  const [directionX, directionY] = {
    ArrowLeft: [-1, 0],
    ArrowRight: [1, 0],
    ArrowUp: [0, -1],
    ArrowDown: [0, 1],
  }[key] ?? [0, 0];

  const candidates = [...points.entries()]
    .filter(([id]) => id !== currentId)
    .map(([id, point]) => {
      const deltaX = point[0] - current[0];
      const deltaY = point[1] - current[1];
      const forward = deltaX * directionX + deltaY * directionY;
      const sideways = Math.abs(deltaX * directionY - deltaY * directionX);
      const distance = Math.hypot(deltaX, deltaY);
      return { id, forward, score: distance * (1 + 3 * sideways / Math.max(forward, 1)) };
    })
    .filter((candidate) => candidate.forward > 2)
    .sort((a, b) => a.score - b.score);
  return candidates[0]?.id ?? null;
}

function ariaLabel(feature: NeighborhoodFeature, rating: Rating | undefined): string {
  return rating === undefined
    ? `${feature.properties.name}, not rated`
    : `${feature.properties.name}, rated ${TIER_BY_VALUE[rating].label}`;
}
